import { Button } from "../../ui/components/Button";
import { Dialog, DialogTrigger } from "../../ui/components/Dialog";
import { Tooltip, TooltipTrigger } from "../../ui/components/Tooltip";
import { Eye, Info, X } from "lucide-react";
import { Modal } from "react-aria-components";
import type { EditorValues, UploadedImage } from "../types";
import { ZoomableImage } from "./ZoomableImage";
import { Histogram } from "./Histogram";

interface ImagePreviewProps {
  image: UploadedImage;
  values: EditorValues;
  onClose?: () => void;
  originalImageUrl?: string;
  currentImageUrl?: string;
  isShowingOriginal?: boolean;
  onToggleOriginal?: () => void;
}

export function ImagePreview({
  values,
  onClose,
  originalImageUrl,
  currentImageUrl,
  isShowingOriginal = false,
  onToggleOriginal,
}: ImagePreviewProps) {
  // Determine which image URL to show
  const currentSrc = isShowingOriginal && originalImageUrl ? originalImageUrl : currentImageUrl;

  return (
    <div className="flex h-full w-full flex-col">
      {/* Image Container - Full Height */}
      <div className="relative flex-1 overflow-hidden rounded-lg shadow-lg">
        <ZoomableImage
          src={currentSrc || ""}
          alt="Edited image preview"
          className="h-full w-full"
          fillContainer={true}
          maxWidth="100%"
          maxHeight="100%"
        />

        {/* Histogram Overlay - shows histogram of currently displayed image */}
        <Histogram previewUrl={currentSrc} overlay={true} />

        {/* Close Button with Confirmation */}
        {onClose && (
          <DialogTrigger>
            <TooltipTrigger delay={300}>
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 h-auto border border-border/50 bg-background/90 p-1 text-foreground backdrop-blur-sm hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </Button>
              <Tooltip>Close image</Tooltip>
            </TooltipTrigger>
            <Modal className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <Dialog className="rounded-lg bg-background p-6 shadow-lg max-w-md w-full mx-4 md:mx-0">
                {({ close }) => (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h3 className="font-semibold text-lg">Close image?</h3>
                      <p className="text-muted-foreground text-sm">
                        Close this image? You will lose any unsaved edits.
                      </p>
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button variant="outline" onPress={close}>
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        onPress={() => {
                          onClose();
                          close();
                        }}
                      >
                        Close image
                      </Button>
                    </div>
                  </div>
                )}
              </Dialog>
            </Modal>
          </DialogTrigger>
        )}

        {/* Editor Values Info Button - Desktop: Tooltip on hover, Mobile: Dialog on tap */}

        {/* Desktop: Tooltip */}
        <TooltipTrigger delay={300}>
          <Button
            variant="ghost"
            size="sm"
            className="hidden md:flex absolute top-2 left-2 h-auto border border-border/50 bg-background/90 p-1 text-foreground backdrop-blur-sm hover:bg-muted"
          >
            <Info className="h-4 w-4" />
          </Button>
          <Tooltip placement="right">
            <div className="max-w-xs space-y-2">
              <h4 className="font-medium text-sm">Current Editor Values</h4>
              <div className="space-y-1 text-xs">
                <div>
                  <strong>Global Light:</strong>
                  <br />
                  Exposure: {values.globalLight.exposure.toFixed(2)}
                  <br />
                  Brightness: {values.globalLight.brightness.toFixed(2)}
                  <br />
                  Contrast: {values.globalLight.contrast.toFixed(2)}
                </div>
                <div>
                  <strong>Regional Light:</strong>
                  <br />
                  Shadows: {values.regionalLight.shadows.toFixed(2)}
                  <br />
                  Highlights: {values.regionalLight.highlights.toFixed(2)}
                  <br />
                  Midtones: {values.regionalLight.midtones.toFixed(2)}
                </div>
                <div>
                  <strong>Color:</strong>
                  <br />
                  Saturation: {values.color.saturation.toFixed(2)}
                  <br />
                  Temperature: {values.color.temperature.toFixed(0)}K<br />
                  Tint: {values.color.tint.toFixed(2)}
                </div>
                <div>
                  <strong>Tonal Curve:</strong>
                  <br />
                  {values.tonalCurve.points.map((point, i) => (
                    <span key={`${point.x}-${point.y}`}>
                      ({point.x.toFixed(2)}, {point.y.toFixed(2)})
                      {i < values.tonalCurve.points.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </div>
                <div>
                  <strong>Color Grading:</strong>
                  <br />
                  Shadows: H{values.colorGrading.shadows.hue.toFixed(0)}° S
                  {(values.colorGrading.shadows.saturation * 100).toFixed(0)}% B
                  {(values.colorGrading.shadows.blend * 100).toFixed(0)}%<br />
                  Midtones: H{values.colorGrading.midtones.hue.toFixed(0)}° S
                  {(values.colorGrading.midtones.saturation * 100).toFixed(0)}% B
                  {(values.colorGrading.midtones.blend * 100).toFixed(0)}%<br />
                  Highlights: H{values.colorGrading.highlights.hue.toFixed(0)}° S
                  {(values.colorGrading.highlights.saturation * 100).toFixed(0)}% B
                  {(values.colorGrading.highlights.blend * 100).toFixed(0)}%
                </div>
              </div>
            </div>
          </Tooltip>
        </TooltipTrigger>

        {/* Mobile: Dialog */}
        <DialogTrigger>
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden absolute top-2 left-2 h-auto border border-border/50 bg-background/90 p-1 text-foreground backdrop-blur-sm hover:bg-muted"
          >
            <Info className="h-4 w-4" />
          </Button>
          <Modal className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <Dialog className="rounded-lg bg-background p-6 shadow-lg max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
              {({ close }) => (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-lg">Current Editor Values</h4>
                    <Button variant="ghost" size="sm" onPress={close} className="h-6 w-6 p-0">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div>
                      <strong>Global Light:</strong>
                      <div className="ml-2 text-muted-foreground">
                        Exposure: {values.globalLight.exposure.toFixed(2)}
                        <br />
                        Brightness: {values.globalLight.brightness.toFixed(2)}
                        <br />
                        Contrast: {values.globalLight.contrast.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <strong>Regional Light:</strong>
                      <div className="ml-2 text-muted-foreground">
                        Shadows: {values.regionalLight.shadows.toFixed(2)}
                        <br />
                        Highlights: {values.regionalLight.highlights.toFixed(2)}
                        <br />
                        Midtones: {values.regionalLight.midtones.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <strong>Color:</strong>
                      <div className="ml-2 text-muted-foreground">
                        Saturation: {values.color.saturation.toFixed(2)}
                        <br />
                        Temperature: {values.color.temperature.toFixed(0)}K<br />
                        Tint: {values.color.tint.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <strong>Tonal Curve:</strong>
                      <div className="ml-2 text-muted-foreground">
                        {values.tonalCurve.points.map((point, i) => (
                          <span key={`${point.x}-${point.y}`}>
                            ({point.x.toFixed(2)}, {point.y.toFixed(2)})
                            {i < values.tonalCurve.points.length - 1 ? ", " : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <strong>Color Grading:</strong>
                      <div className="ml-2 text-muted-foreground text-xs">
                        Shadows: H{values.colorGrading.shadows.hue.toFixed(0)}° S
                        {(values.colorGrading.shadows.saturation * 100).toFixed(0)}% B
                        {(values.colorGrading.shadows.blend * 100).toFixed(0)}%<br />
                        Midtones: H{values.colorGrading.midtones.hue.toFixed(0)}° S
                        {(values.colorGrading.midtones.saturation * 100).toFixed(0)}% B
                        {(values.colorGrading.midtones.blend * 100).toFixed(0)}%<br />
                        Highlights: H{values.colorGrading.highlights.hue.toFixed(0)}° S
                        {(values.colorGrading.highlights.saturation * 100).toFixed(0)}% B
                        {(values.colorGrading.highlights.blend * 100).toFixed(0)}%
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Dialog>
          </Modal>
        </DialogTrigger>

        {/* Show Original Button - Bottom Right */}
        {originalImageUrl && (
          <TooltipTrigger delay={300}>
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-2 bottom-2 h-auto border border-border/50 bg-background/90 p-1 text-foreground backdrop-blur-sm hover:bg-muted"
              onPressStart={() => onToggleOriginal?.()}
              onPressEnd={() => onToggleOriginal?.()}
              onPressChange={() => onToggleOriginal?.()}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Tooltip placement="left">Hold to show original</Tooltip>
          </TooltipTrigger>
        )}
      </div>
    </div>
  );
}
